import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { TrendingUp, ArrowLeft } from "lucide-react";

const HowToGetMoreLawnCareLeads = () => {
  return (
    <>
      <SEO 
        title="How to Get More Lawn Care Leads Online in 2024"
        description="Proven strategies to generate more lawn care leads through your website. Learn how to attract local customers and grow your lawn care business online."
        keywords="lawn care leads, get lawn care customers online, lawn care marketing, websites for lawn care"
        canonical="https://www.leafrushmarketing.com/resources/how-to-get-more-lawn-care-leads-online/"
        ogUrl="https://www.leafrushmarketing.com/resources/how-to-get-more-lawn-care-leads-online/"
      />
      <article className="flex flex-col">
        {/* Back Link */}
        <section className="py-6 border-b">
          <div className="container px-4 md:px-6">
            <NavLink to="/resources" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Resources
            </NavLink>
          </div>
        </section>

        {/* Article Header */}
        <section className="py-12 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm font-medium text-primary">LEAD GENERATION</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                How to Get More Lawn Care Leads Online
              </h1>
              <p className="text-xl text-muted-foreground">
                Stop relying on word-of-mouth and door hangers. Here's how to use your website and online presence 
                to consistently generate qualified lawn care leads in your local area.
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-12 md:pb-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto prose prose-lg">
              <h2 className="text-2xl md:text-3xl font-bold mt-8 mb-4">1. Start with a Website That Converts</h2>
              
              <p className="text-muted-foreground mb-4">
                Your website is your 24/7 salesperson. It needs to make a great first impression, clearly explain your services, 
                and make it ridiculously easy for visitors to contact you.
              </p>

              <p className="text-muted-foreground mb-6">
                <strong>What makes a lawn care website convert?</strong>
              </p>

              <ul className="space-y-2 text-muted-foreground mb-6">
                <li>Professional design that builds trust instantly</li>
                <li>Before/after photos proving the quality of your work</li>
                <li>Clear service descriptions and transparent pricing guidance</li>
                <li>Prominent phone numbers and quote request forms</li>
                <li>Customer reviews and testimonials displayed prominently</li>
                <li>Mobile-friendly design (most visitors are on phones)</li>
              </ul>

              <p className="text-muted-foreground mb-6">
                Without a strong website foundation, all your other marketing efforts will underperform.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">2. Optimize for Local Search (Local SEO)</h2>
              
              <p className="text-muted-foreground mb-4">
                When homeowners search "lawn care near me" or "lawn mowing service [your city]," you want your business to show up. 
                That's where local SEO comes in.
              </p>

              <p className="text-muted-foreground mb-6">
                <strong>Local SEO essentials:</strong>
              </p>

              <ul className="space-y-2 text-muted-foreground mb-6">
                <li><strong>Claim your Google Business Profile:</strong> This is the #1 priority. Keep it updated with photos, hours, and reviews.</li>
                <li><strong>Use local keywords:</strong> Mention your city and neighborhoods throughout your website naturally.</li>
                <li><strong>Get customer reviews:</strong> More 5-star reviews = higher rankings and more trust.</li>
                <li><strong>Build local citations:</strong> List your business on Yelp, Angi, Thumbtack, and local directories.</li>
                <li><strong>Create location-specific pages:</strong> If you serve multiple towns, create dedicated pages for each area.</li>
              </ul>

              <p className="text-muted-foreground mb-6">
                Local SEO takes time but delivers consistent, organic leads month after month without ongoing ad spend.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">3. Run Targeted Google Ads</h2>
              
              <p className="text-muted-foreground mb-4">
                Want leads faster? Google Ads puts you at the top of search results immediately. 
                When done right, you pay only when someone clicks—and if your website converts, those clicks turn into paying customers.
              </p>

              <p className="text-muted-foreground mb-6">
                <strong>Google Ads strategy for lawn care:</strong>
              </p>

              <ul className="space-y-2 text-muted-foreground mb-6">
                <li>Target high-intent keywords like "lawn care service [city]" and "hire lawn mowing company"</li>
                <li>Set geographic targeting to only your service area (don't waste budget on areas you don't serve)</li>
                <li>Use ad extensions to show your phone number, location, and customer ratings</li>
                <li>Track conversions (calls, form fills) so you know exactly what's working</li>
                <li>Start small and scale up as you see results</li>
              </ul>

              <p className="text-muted-foreground mb-6">
                Google Ads works especially well during peak season (spring/summer) when demand is high and customers are ready to hire.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">4. Showcase Your Work on Social Media</h2>
              
              <p className="text-muted-foreground mb-4">
                Social media won't replace your website, but it's a great way to stay visible, build credibility, 
                and remind people in your area that you exist.
              </p>

              <ul className="space-y-2 text-muted-foreground mb-6">
                <li><strong>Post before/after photos regularly:</strong> Visual proof is powerful.</li>
                <li><strong>Share lawn care tips:</strong> Position yourself as the local expert.</li>
                <li><strong>Encourage reviews and tags:</strong> Social proof drives more inquiries.</li>
                <li><strong>Run local Facebook ads:</strong> Target homeowners in your service area with special offers.</li>
              </ul>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">5. Make It Easy to Contact You</h2>
              
              <p className="text-muted-foreground mb-4">
                This seems obvious, but you'd be surprised how many lawn care websites make it hard to get in touch.
              </p>

              <ul className="space-y-2 text-muted-foreground mb-6">
                <li>Display your phone number prominently on every page</li>
                <li>Add click-to-call buttons for mobile visitors</li>
                <li>Use a simple contact form (name, email, phone, service needed)</li>
                <li>Respond quickly—within hours, not days</li>
                <li>Consider live chat or automated booking for instant engagement</li>
              </ul>

              <p className="text-muted-foreground mb-6">
                The faster and easier you make it to contact you, the more leads you'll close.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Need Help Getting More Lawn Care Leads?</h2>
              
              <p className="text-muted-foreground mb-6">
                At Leaf Rush Marketing, we build websites for lawn care and landscaping businesses that are designed to generate leads—not just look pretty. 
                Our sites are optimized for local search, mobile-friendly, and built to convert visitors into customers.
              </p>

              <p className="text-muted-foreground mb-8">
                We also offer Google Ads management and Google Business Profile optimization to help you get even more leads. 
                And with our $0 upfront model, you only start paying once you love your site and it goes live.
              </p>

              <div className="not-prose">
                <NavLink to="/contact#get-started">
                  <Button size="lg" className="text-lg px-8">
                    Get Started
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default HowToGetMoreLawnCareLeads;
